/**
 * Undo/redo: an immutable history of scene snapshots for the session. Scenes are immutable values with
 * structural sharing (commands copy only the path they change), so a snapshot costs little.
 *
 * - commit: a new entry; with a merge key, edits within MERGE_MS of the previous edit with the same key replace it
 *   (typing in one inspector field is one undo step);
 * - preview/commitFrom: a drag shows intermediate scenes without entries and commits once at the end.
 */
export const MERGE_MS = 1000;
export const LIMIT = 300;

export interface History<T> {
  past: T[];
  present: T;
  future: T[];
  lastKey?: string | undefined;
  lastAt?: number | undefined;
}

export function createHistory<T>(present: T): History<T> {
  return { past: [], present, future: [] };
}

export function commit<T>(h: History<T>, next: T, opts: { key?: string; now?: number } = {}): History<T> {
  if (next === h.present) return h;
  const now = opts.now ?? Date.now();
  if (opts.key !== undefined && opts.key === h.lastKey && h.lastAt !== undefined && now - h.lastAt < MERGE_MS) {
    return { ...h, present: next, future: [], lastAt: now };
  }
  const past = [...h.past, h.present];
  if (past.length > LIMIT) past.splice(0, past.length - LIMIT);
  return { past, present: next, future: [], lastKey: opts.key, lastAt: now };
}

/** Show a scene without recording it (during a drag). */
export function preview<T>(h: History<T>, next: T): History<T> {
  return next === h.present ? h : { ...h, present: next };
}

/** Record the end of a drag: one entry from `base` (the scene before the drag) to `next`. */
export function commitFrom<T>(h: History<T>, base: T, next: T): History<T> {
  if (next === base) return { ...h, present: base };
  const past = [...h.past, base];
  if (past.length > LIMIT) past.splice(0, past.length - LIMIT);
  return { past, present: next, future: [], lastKey: undefined, lastAt: undefined };
}

export const canUndo = <T>(h: History<T>) => h.past.length > 0;
export const canRedo = <T>(h: History<T>) => h.future.length > 0;

export function undo<T>(h: History<T>): History<T> {
  const prev = h.past[h.past.length - 1];
  if (prev === undefined) return h;
  return { past: h.past.slice(0, -1), present: prev, future: [h.present, ...h.future], lastKey: undefined, lastAt: undefined };
}

export function redo<T>(h: History<T>): History<T> {
  const next = h.future[0];
  if (next === undefined) return h;
  return { past: [...h.past, h.present], present: next, future: h.future.slice(1), lastKey: undefined, lastAt: undefined };
}

/** Replace the whole history (after a reload from the server). */
export function reset<T>(present: T): History<T> {
  return createHistory(present);
}
